import * as React from "react";
import { ApiInstance, ApiService, normalizeApiUrl } from "../_Services/ApiService";
import { ApiDiscoveryService } from "../_Services/ApiDiscoveryService";
import { observable, action, computed } from "mobx";
import { observer } from "mobx-react";
import Button from "@material-ui/core/Button";
import { TextField } from "../_Components/TextField";
import { HeartbeatService } from "../_Services/HeartbeatService";
import { Layout, LayoutHeader, LayoutContent } from "../_Components/Layout";
import { AppRouter } from "../AppRouter";
import { SettingsService } from "../_Services/SettingsService";
import { autoinject } from "aurelia-dependency-injection";
import { r } from "../_Services/LanguageService";
import { AppState } from "../AppState";
import { once } from "../_Common/once";
import { FormControlLabel, Radio, RadioGroup } from "@material-ui/core";

@autoinject()
export class InstallationPageModel {

    constructor(
        public apiService: ApiService,
        private apiDiscoveryService: ApiDiscoveryService,
        private heartbeatService: HeartbeatService,
        public appState: AppState,
        private appRouter: AppRouter,
        private settingsService: SettingsService) {
    }

    @observable inProgress: boolean = false;
    @observable isOk: boolean = false;
    /** Url typed by the user - used only when no instance list is deployed. */
    @observable url: string = "";
    /** Name of the instance picked from the deployed/discovered list. */
    @observable instanceName: string = "";
    @observable currentInstallationToken: string = "";
    @observable installationToken: string = "";
    @observable deviceName: string = "";
    @observable message: string = "";

    /** True when the target is picked from a list instead of typed. */
    @computed get hasInstanceSelection(): boolean {
        return this.apiService.hasInstanceSelection;
    }

    /** Instances offered by the urls discovery endpoint or by the static apiMap. */
    @computed get instances(): ApiInstance[] {
        return this.apiService.isDiscoveryEnabled
            ? this.apiDiscoveryService.instances.slice()
            : this.apiService.staticApiInstances;
    }

    @computed get selectedInstance(): ApiInstance {
        return this.instances.find(i => i.name === this.instanceName);
    }

    @computed get isWaitingForInstances(): boolean {
        return this.hasInstanceSelection && this.instances.length === 0;
    }

    @computed get isValid() {
        const hasTarget = this.hasInstanceSelection ? !!this.selectedInstance : this.url.length > 0;
        return hasTarget && this.installationToken && this.installationToken.length > 5 && this.deviceName;
    }

    @action
    setUrl(value: string) {
        this.url = value;
    }

    @action
    setInstanceName(value: string) {
        this.instanceName = value;
    }

    @action
    setInstallationToken(value: string) {
        this.installationToken = value;
    }

    @action
    setDeviceName(value: string) {
        this.deviceName = value;
    }

    @action
    load() {
        this.url = this.settingsService.apiRoot || this.apiService.defaultApiRoot || "";
        this.instanceName = this.settingsService.apiInstanceName || "";
        this.deviceName = this.settingsService.deviceName || "";
        this.currentInstallationToken = this.settingsService.installationToken || "";
        this.installationToken = "";
    }

    @action.bound
    async registerDevice() {
        const instance = this.hasInstanceSelection ? this.selectedInstance : null;
        const apiRoots = instance
            ? instance.urls.slice()
            : [normalizeApiUrl(this.url)].filter(i => i !== "");

        if (apiRoots.length === 0 || this.installationToken.length === 0 || this.deviceName.length === 0) {
            alert("URL should point to API and include Installation Token and Device Name");
            return;
        }
        this.inProgress = true;
        this.apiService.setEndpoints(instance ? instance.name : null, apiRoots);
        const response = await this.apiService.install(this.currentInstallationToken, this.installationToken, this.settingsService.deviceHardwareId, this.deviceName);

        this.isOk = response.IsOk;
        this.message = response.Message;

        if (response.IsOk === true) {
            setTimeout(() => {
                this.setup(instance, apiRoots, response.ApiUrl, response.ApiToken, response.DeviceId);
                this.heartbeatService.downloadConfiguration().then(() => {
                    this.inProgress = false;
                    this.appRouter.Settings.navigator({}).navigate();
                });
                this.inProgress = false;
            }, 2000);
        } else {
            this.inProgress = false;
            alert(response.Message);
        }
    }

    @action.bound goBack() {
        this.appRouter.navigateHome();
    }

    private setup(instance: ApiInstance, apiRoots: string[], responseApiUrl: string, secret: string, deviceId: string) {
        // with an instance list the deployment owns the urls, so the single url
        // returned by the server must not replace them
        const roots = instance
            ? apiRoots
            : [normalizeApiUrl(responseApiUrl) || apiRoots[0]];

        this.settingsService.update(s => {
            s.apiInstanceName = instance ? instance.name : null;
            s.apiRoots = roots;
            s.apiRoot = roots[0];
            s.apiSecret = secret;
            s.deviceId = deviceId;
            s.deviceName = this.deviceName;
            s.installationToken = this.installationToken;
        });
    }

    @computed get hasInstallation(): boolean {
        return !!this.settingsService.apiSecret;
    }
}

export interface InstallationPageProps {
    model: InstallationPageModel;
}

@once((props: InstallationPageProps) => {
    props.model.load();
})
@observer
export class InstallationPage extends React.Component<InstallationPageProps> {
    render() {
        const { model } = this.props;
        return (
            <Layout>
                <LayoutHeader title={r("Device Registration")} goBack={model.goBack} isBackDisabled={!model.hasInstallation} />
                <LayoutContent>
                    {
                        !model.inProgress && <div className="simple-form">
                            {
                                model.hasInstanceSelection === false && <TextField
                                    title={r("URL for server API")}
                                    type="url"
                                    getValue={() => model.url}
                                    setValue={v => model.setUrl(v)}
                                />
                            }
                            {
                                model.hasInstanceSelection && <RadioGroup
                                    value={model.instanceName}
                                    onChange={(e: any) => model.setInstanceName(e.target.value)}
                                >
                                    {
                                        model.instances.map(i =>
                                            <FormControlLabel key={i.name} value={i.name} control={<Radio />} label={i.name} />
                                        )
                                    }
                                </RadioGroup>
                            }
                            {
                                model.isWaitingForInstances && <div className="installation-message">
                                    {r("Looking for available servers...")}
                                </div>
                            }
                            <TextField
                                title={r("Installation Token")}
                                getValue={() => model.installationToken}
                                setValue={v => model.setInstallationToken(v)}
                            />
                            <TextField
                                title={r("Device Name")}
                                getValue={() => model.deviceName}
                                setValue={v => model.setDeviceName(v)}
                            />
                            <div className="installation-actions">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={model.registerDevice}
                                    disabled={!model.isValid}
                                >
                                    {r("Register Device")}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={model.goBack}
                                    disabled={!model.hasInstallation}
                                >
                                    {r("Back to Scanning Page")}
                                </Button>
                            </div>
                            <div className="version installation-version">{model.appState.version}</div>
                        </div>
                    }
                    {
                        model.inProgress && <div className="center-container">
                            <div className={`circle-loader ${model.isOk ? "load-complete" : ""}`}>
                                <div className="checkmark draw"></div>
                            </div>
                            <div>{model.message}</div>
                        </div>
                    }
                </LayoutContent>
            </Layout>
        );
    }
}
